/**
 * Permission areas for granular access control.
 * Each key maps to a section/feature in the dashboard.
 */
export const PERMISSION_KEYS = [
  "team_documents",
  "certificates",
  "client_directory",
  "action_centre",
  "ndis_reviews",
  "reports",
  "fca_builder",
  "enquiries",
  "echo",
  "team_access_admin",
] as const;

export type PermissionKey = (typeof PERMISSION_KEYS)[number];

export const PERMISSION_LABELS: Record<PermissionKey, string> = {
  team_documents: "Team Documents",
  certificates: "Certificates",
  client_directory: "Client Directory",
  action_centre: "Action Centre",
  ndis_reviews: "NDIS Reviews",
  reports: "Reports",
  fca_builder: "FCA Builder",
  enquiries: "Enquiries",
  echo: "Echo",
  team_access_admin: "Team Access (Admin)",
};

export const PERMISSION_DESCRIPTIONS: Record<PermissionKey, string> = {
  team_documents: "Onboarding docs, mentoring guide, support loop, development plans",
  certificates: "Upload and view clearances & certificates",
  client_directory: "View/manage client list, send planners",
  action_centre: "Birthday, check-in, and report reminders",
  ndis_reviews: "Review dashboard and reminder history",
  reports: "Progress report workflow",
  fca_builder: "Functional capacity assessments",
  enquiries: "Website form submissions",
  echo: "Echo stories and character bible",
  team_access_admin: "Manage who can log in (admin only)",
};

/**
 * Extended permissions type that supports nested certificate access.
 * The `certificates_access` field is an array of allowed_emails IDs
 * that this user can view certificates for. "own" is always implicit.
 * An empty array or undefined means "own only" when certificates is true.
 * "all" means they can see everyone's certificates.
 */
export type PermissionsMap = Record<PermissionKey, boolean> & {
  certificates_access?: number[] | "all";
};

/** All permissions enabled — used for Full Access */
export function allPermissions(): PermissionsMap {
  const perms = Object.fromEntries(PERMISSION_KEYS.map(k => [k, true])) as unknown as PermissionsMap;
  perms.certificates_access = "all";
  return perms;
}

/** No permissions enabled — used as starting point for Limited Access */
export function noPermissions(): PermissionsMap {
  const perms = Object.fromEntries(PERMISSION_KEYS.map(k => [k, false])) as unknown as PermissionsMap;
  perms.certificates_access = [];
  return perms;
}

/**
 * Route-to-permission mapping.
 * Maps route prefixes to the permission key required to access them.
 */
export const ROUTE_PERMISSIONS: Record<string, PermissionKey> = {
  "/emma": "team_documents",
  "/maddy": "team_documents",
  "/jade": "team_documents",
  "/sophia": "team_documents",
  "/mentoring-guide": "team_documents",
  "/support-loop": "team_documents",
  "/development-plan": "team_documents",
  "/careers": "team_documents",
  "/certificates": "certificates",
  "/admin": "client_directory",
  "/action-centre": "action_centre",
  "/ndis-reviews": "ndis_reviews",
  "/reports": "reports",
  "/fca-list": "fca_builder",
  "/fca-builder": "fca_builder",
  "/admin/enquiries": "enquiries",
  "/echo": "echo",
  "/team-access": "team_access_admin",
};
